// ============================================
// SEO COMPONENT — Glitz & Glam (enhanced)
//
// Adds on top of the original:
//   • breadcrumbs  → BreadcrumbList JSON-LD + Google breadcrumb trail in search
//   • faqs         → FAQPage JSON-LD (expanding rich results)
//   • og:locale:alternate for Hindi (bilingual signal)
//   • hreflang alternates with x-default (for bilingual content routing)
//   • Each JSON-LD block now uses its own script tag id → no overwriting
//
// Fully backward compatible — existing <SEO title=... /> calls still work.
// ============================================

import { useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || 'https://api.glitzandglambyjyoti.com';

const DEFAULT = {
  title:       'Glitz & Glam by Jyoti — Premium Artificial Jewelry & Fashion',
  description: 'Shop artificial jewelry, bangles, hair accessories, sarees and fashion items for women. Premium quality products delivered across India.',
  keywords:    'artificial jewelry, bangles, earrings, necklace, hair accessories, sarees, women fashion India',
  image:       'https://glitzandglambyjyoti.com/og-image.jpg',
  url:         'https://glitzandglambyjyoti.com',
};

let _seoCache = null;
const loadSEOSettings = async () => {
  if (_seoCache) return _seoCache;
  try {
    const res = await fetch(`${API}/api/settings/seo`);
    if (res.ok) { _seoCache = await res.json(); return _seoCache; }
  } catch(e) {}
  return {};
};

// ── DOM helpers ────────────────────────────────────────────────────
const setMeta = (name, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
  el.setAttribute('content', content);
};

const setOG = (property, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
  el.setAttribute('content', content);
};

// Allow multiple og:locale:alternate tags (Facebook supports repeated properties)
const addOGOnce = (property, content) => {
  if (!content) return;
  const existing = document.querySelectorAll(`meta[property="${property}"]`);
  for (const e of existing) if (e.getAttribute('content') === content) return;
  const el = document.createElement('meta');
  el.setAttribute('property', property);
  el.setAttribute('content', content);
  document.head.appendChild(el);
};

const setCanonical = (url) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', 'canonical'); document.head.appendChild(el); }
  el.setAttribute('href', url);
};

// Each JSON-LD block gets its own <script> tag, keyed by ID
const setJsonLd = (id, data) => {
  let el = document.querySelector(`#${id}`);
  if (!data) { // remove if no data
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

// Language alternates — hreflang links for Google
const clearAlternates = () => {
  document.querySelectorAll('link[rel="alternate"][data-seo-alt]').forEach(el => el.remove());
};
const addAlternate = (hreflang, href) => {
  const el = document.createElement('link');
  el.setAttribute('rel', 'alternate');
  el.setAttribute('hreflang', hreflang);
  el.setAttribute('href', href);
  el.setAttribute('data-seo-alt', '1');
  document.head.appendChild(el);
};

// Google Analytics — inject once
const injectGA = (gaId) => {
  if (!gaId || document.getElementById('ga-script')) return;
  const s1 = document.createElement('script');
  s1.id = 'ga-script'; s1.async = true;
  s1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(s1);
  const s2 = document.createElement('script');
  s2.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId}');`;
  document.head.appendChild(s2);
};

// Facebook Pixel — inject once
const injectFBPixel = (pixelId) => {
  if (!pixelId || document.getElementById('fb-pixel')) return;
  const s = document.createElement('script');
  s.id = 'fb-pixel';
  s.textContent = `!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window,document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init','${pixelId}');fbq('track','PageView');`;
  document.head.appendChild(s);
};

// ── Component ──────────────────────────────────────────────────────
const SEO = ({
  title, description, keywords, image, url,
  type = 'website',
  product, article,
  breadcrumbs,       // [{ name, url }]  e.g. Home > Shop > Earrings > Pearl Drops
  faqs,              // [{ question, answer }]  FAQ-style rich results
  additionalSchemas, // [obj, ...] extra JSON-LD schemas to inject (e.g. Organization)
  noindex = false,
}) => {
  useEffect(() => {
    const apply = async () => {
      const settings = await loadSEOSettings();
      const t       = title       || settings.siteTitle       || DEFAULT.title;
      const desc    = description || settings.siteDescription || DEFAULT.description;
      const kw      = keywords    || settings.siteKeywords    || DEFAULT.keywords;
      const img     = image       || settings.ogImage         || DEFAULT.image;
      const base    = settings.canonicalUrl || DEFAULT.url;
      const pageUrl = url ? `${base}${url}` : base;

      // Analytics / tracking
      if (settings.googleAnalyticsId) injectGA(settings.googleAnalyticsId);
      if (settings.facebookPixelId)   injectFBPixel(settings.facebookPixelId);
      if (settings.googleVerification) {
        setMeta('google-site-verification', settings.googleVerification);
      }

      // Core tags
      document.title = t;
      setMeta('description', desc);
      setMeta('keywords',    kw);
      setMeta('robots',      noindex ? 'noindex, nofollow' : 'index, follow');
      setMeta('author',      'Glitz & Glam by Jyoti');
      setMeta('theme-color', '#c9a84c');

      // Open Graph
      setOG('og:title',       t);
      setOG('og:description', desc);
      setOG('og:image',       img);
      setOG('og:url',         pageUrl);
      setOG('og:type',        type);
      setOG('og:site_name',   'Glitz & Glam by Jyoti');
      setOG('og:locale',      'en_IN');
      // Bilingual signal — tells Facebook this content is available in Hindi too
      addOGOnce('og:locale:alternate', 'hi_IN');

      // Twitter
      setMeta('twitter:card',        'summary_large_image');
      setMeta('twitter:title',       t);
      setMeta('twitter:description', desc);
      setMeta('twitter:image',       img);
      setMeta('twitter:site',        '@glitz_and_glam_by_jyoti');

      // Canonical + hreflang alternates
      setCanonical(pageUrl);
      clearAlternates();
      addAlternate('en-IN',     pageUrl);
      addAlternate('hi-IN',     `${pageUrl}${pageUrl.includes('?') ? '&' : '?'}lang=hi`);
      addAlternate('x-default', pageUrl);

      // ── Main JSON-LD (Product | Article | Organization) ─────────
      if (product) {
        setJsonLd('jsonld-main', {
          '@context': 'https://schema.org',
          '@type':    'Product',
          name:         product.name,
          description:  product.description || desc,
          image:        product.images?.length ? product.images : [img],
          sku:          product._id || product.sku,
          brand:        { '@type': 'Brand', name: 'Glitz & Glam by Jyoti' },
          category:     product.category,
          offers: {
            '@type':         'Offer',
            price:           product.discountPrice || product.price,
            priceCurrency:   'INR',
            availability:    product.status === 'soldout'
                               ? 'https://schema.org/OutOfStock'
                               : 'https://schema.org/InStock',
            url:             pageUrl,
            priceValidUntil: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
            seller:          { '@type': 'Organization', name: 'Glitz & Glam by Jyoti' },
            shippingDetails: {
              '@type': 'OfferShippingDetails',
              shippingRate: { '@type': 'MonetaryAmount', value: '0', currency: 'INR' },
              shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'IN' },
              deliveryTime: {
                '@type': 'ShippingDeliveryTime',
                handlingTime:   { '@type': 'QuantitativeValue', minValue: 0, maxValue: 2, unitCode: 'DAY' },
                transitTime:    { '@type': 'QuantitativeValue', minValue: 2, maxValue: 7, unitCode: 'DAY' }
              }
            }
          },
          ...(product.numReviews > 0 && {
            aggregateRating: {
              '@type':      'AggregateRating',
              ratingValue:  product.rating || 4.5,
              reviewCount:  product.numReviews
            }
          }),
        });
      } else if (article) {
        setJsonLd('jsonld-main', {
          '@context':  'https://schema.org',
          '@type':     'Article',
          headline:    t,
          description: desc,
          image:       img,
          author:      { '@type': 'Person', name: article.author || 'Jyoti' },
          publisher: {
            '@type': 'Organization',
            name:    'Glitz & Glam by Jyoti',
            logo:    { '@type': 'ImageObject', url: `${base}/logo512.png` }
          },
          datePublished: article.createdAt,
          dateModified:  article.updatedAt || article.createdAt,
          mainEntityOfPage: pageUrl,
          url:           pageUrl,
        });
      } else {
        setJsonLd('jsonld-main', {
          '@context': 'https://schema.org',
          '@type':    'Organization',
          name:        'Glitz & Glam by Jyoti',
          url:         base,
          logo:        `${base}/logo512.png`,
          description: DEFAULT.description,
          sameAs:      ['https://www.instagram.com/glitz_and_glam_by_jyoti'],
          contactPoint: {
            '@type':           'ContactPoint',
            contactType:       'Customer Service',
            availableLanguage: ['English', 'Hindi']
          }
        });
      }

      // ── Breadcrumb JSON-LD ──────────────────────────────────────
      // Props example:
      //   breadcrumbs={[
      //     { name: 'Home',     url: '/' },
      //     { name: 'Shop',     url: '/shop' },
      //     { name: 'Earrings', url: '/shop?category=earrings' },
      //     { name: product.name, url: `/product/${product._id}` }
      //   ]}
      if (breadcrumbs && breadcrumbs.length > 0) {
        setJsonLd('jsonld-breadcrumb', {
          '@context':        'https://schema.org',
          '@type':           'BreadcrumbList',
          itemListElement:   breadcrumbs.map((b, i) => ({
            '@type':  'ListItem',
            position: i + 1,
            name:     b.name,
            item:     b.url.startsWith('http') ? b.url : `${base}${b.url}`
          }))
        });
      } else {
        setJsonLd('jsonld-breadcrumb', null);
      }

      // ── FAQ JSON-LD ─────────────────────────────────────────────
      // Props example (on PolicyPage or ProductDetail):
      //   faqs={[
      //     { question: 'How long does shipping take?',
      //       answer:   'Orders ship within 2 business days and typically arrive within 5–7 days across India.' },
      //     { question: 'Can I return an item?',
      //       answer:   'Yes, you can return within 7 days of delivery. See our return policy.' }
      //   ]}
      if (faqs && faqs.length > 0) {
        setJsonLd('jsonld-faq', {
          '@context':  'https://schema.org',
          '@type':     'FAQPage',
          mainEntity:  faqs.map(f => ({
            '@type': 'Question',
            name:    f.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text:    f.answer
            }
          }))
        });
      } else {
        setJsonLd('jsonld-faq', null);
      }

      // ── Additional schemas (Organization, Store, LocalBusiness, Review, etc.) ──
      // Props example:
      //   additionalSchemas={[organizationSchema, localBusinessSchema]}
      // Each entry is a raw JSON-LD object. Multiple schemas get distinct DOM ids.
      // Clear up to 5 previous additional schemas then re-inject current ones.
      for (let i = 0; i < 5; i++) setJsonLd(`jsonld-additional-${i}`, null);
      if (Array.isArray(additionalSchemas)) {
        additionalSchemas.slice(0, 5).forEach((schema, i) => {
          if (schema && typeof schema === 'object') {
            setJsonLd(`jsonld-additional-${i}`, schema);
          }
        });
      }
    };
    apply();
  }, [title, description, keywords, image, url, type, product, article, breadcrumbs, faqs, additionalSchemas, noindex]);

  return null;
};

export default SEO;
